import {
  AddCircleOutlineRounded,
  HelpOutlineRounded,
} from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import { DistaTooltip, DistaTooltipVariant } from "@sista/dista-ui";
import { IdmClientContext } from "@sista/idm-client";
import {
  Organization,
  RelationDTO,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { OrganizationPicker } from "./OrganizationPicker";
import { OrganizationRelationToOrganizationFounded } from "./OrganizationRelationToOrganizationFounded";
import { OrganizationSearchByIcoContent } from "./OrganizationSearchByIcoContent";
import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";

export type OrganizationRejstrikSearchContentProps = {
  onFoundOrganization: (organization: Organization) => void;
  onFoundRelationsInOrganization: (relations: RelationDTO[]) => void;
  isForeignCompanyChecked: (isForeignCompany: boolean) => void;
};

type Mode = "search" | "createNew";

export const OrganizationRejstrikSearchContent = (
  props: OrganizationRejstrikSearchContentProps
) => {
  const { t } = useTranslation("ucet");
  const { loggedAccount, switchProfile } = useContext(IdmClientContext);
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("search");
  const [showDialogToSwitchProfile, setShowDialogToSwitchProfile] =
    useState(false);

  const rej = useRejstrikClient();

  const [findOrganizationByIdCall] = useSafeSistaAsync(
    rej.findOrganizationById
  );
  const [getMyRelationsInOrganizationCall] = useSafeSistaAsync(
    rej.getMyRelationsInOrganization
  );
  const [
    hasLoggedAnyOrganizationRelationCall,
    hasLoggedAnyOrganizationRelationState,
  ] = useSafeSistaAsync(rej.hasLoggedAnyOrganizationRelation);

  const onSwitchProfile = useCallback(
    (profileId: string) => {
      switchProfile(profileId);
      navigate("/");
    },
    [switchProfile, navigate]
  );

  // --- vyhledání organizace přes globální rejstříkové vyhledávátko ---
  const [pickedOrganizationId, setPickedOrganizationId] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (!pickedOrganizationId) return;
    findOrganizationByIdCall({ organizationId: pickedOrganizationId }).then(
      async (result) => {
        if (typeof result !== "object" || !result) return;
        const organization = result as Organization;
        const profileIdWithRelationToOrganization =
          await hasLoggedAnyOrganizationRelationCall({
            organizationId: organization.id,
          });
        if (profileIdWithRelationToOrganization != null) {
          setShowDialogToSwitchProfile(true);
          return;
        }
        const relations = await getMyRelationsInOrganizationCall({
          organizationId: organization.id,
        });
        props.onFoundRelationsInOrganization(
          typeof relations === "object" && relations ? relations : []
        );
        props.onFoundOrganization(organization);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedOrganizationId]);

  return (
    <>
      {showDialogToSwitchProfile &&
        hasLoggedAnyOrganizationRelationState.value != null && (
          <OrganizationRelationToOrganizationFounded
            profileId={hasLoggedAnyOrganizationRelationState.value.value}
            authToken={loggedAccount.authKey}
            onSwitchProfile={(profileId) => {
              setShowDialogToSwitchProfile(false);
              onSwitchProfile(profileId);
            }}
            onClose={() => setShowDialogToSwitchProfile(false)}
          />
        )}

      {mode === "search" && (
        <Stack direction="column" spacing={2} mt={2}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={0.5}
            alignItems="center"
          >
            <Button
              variant="text"
              startIcon={<AddCircleOutlineRounded />}
              onClick={() => setMode("createNew")}
            >
              {t("ucet.organization.form.createNew", "Založit organizaci")}
            </Button>
            <DistaTooltip
              title={t(
                "ucet.organization.form.createNewTooltip",
                "Nenašli jste svoji organizaci, přidejte ji."
              )}
              variant={DistaTooltipVariant.info}
            >
              <HelpOutlineRounded color="primary" sx={{ width: "16px" }} />
            </DistaTooltip>
          </Stack>
          <OrganizationPicker
            value={pickedOrganizationId}
            onValueChange={(id) => setPickedOrganizationId(id)}
            label={t(
              "ucet.organization.form.searchLabel",
              "Název, IČO, DIČ nebo ID subjektu"
            )}
          />
          <Box sx={{ height: 80 }} />
        </Stack>
      )}

      {mode === "createNew" && (
        <OrganizationSearchByIcoContent
          onFoundOrganization={props.onFoundOrganization}
          onFoundRelationsInOrganization={props.onFoundRelationsInOrganization}
          isForeignCompanyChecked={props.isForeignCompanyChecked}
          onBack={() => setMode("search")}
        />
      )}
    </>
  );
};
